<template>
    <base-dialog :loading="loading" :value="dialog" :width="480" :fullscreen="isMobile"
        :title="editMode ? $t('programs.edit_program') : $t('programs.create_program')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="dlg-fields">
            <kimsa-text-field :label="$t('common.name')" :placeholder="$t('programs.name_placeholder')" dense
                force-value :value="name" @change="name = $event" />
            <div class="prg-grid2">
                <kimsa-combobox :label="$t('common.category')" :placeholder="$t('exercises.pick_one_category')" dense
                    :items="categories" @change="category = $event" :value="category" item-text="label"
                    ref="categories" />
                <kimsa-combobox :label="$t('common.training_per_week')" :placeholder="$t('common.select')" dense
                    :items="daysPerWeekOptions" @change="daysPerWeek = $event" :value="daysPerWeek" ref="daysPerWeek" />
            </div>
            <div v-if="!editMode && !fromScratch" class="prg-grid2">
                <kimsa-date-picker :label="$t('routines.from')" solo flat dense color="secondary" :value="startDate"
                    ref="start" disabled />
                <kimsa-date-picker :disabled="!startDate" :label="$t('routines.to')" solo flat dense color="secondary"
                    :min="startDate" @change="endDate = $event" :value="endDate" ref="end" />
            </div>
            <kimsa-textarea-field :label="$t('common.description')" :placeholder="$t('programs.name_placeholder')"
                force-value :value="description" @change="description = $event" />
            <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                :label="$t('exercises.tags')" force-value />
            <kimsa-upload-file :label="$t('common.image')" relative-path="images/programs" mime-types="image/*"
                @change="file = $event" :preview-img="previewImg" ref="img" height="150" @delete="onDeleteImg" />
        </div>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="$t('common.save')" :disabled="disabled" :loading="loading"
                @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import { clearBlocksAssignedRoutine, getPayloadBaseProgram } from "@/utils/Exercises";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";
import KimsaTagsPicker from '../../common/inputs/KimsaTagsPicker.vue';
import { uniqueDayRoutine } from "../../../utils/Exercises";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";

export default {
    name: "SaveProgramDialog",
    components: { KimsaTextareaField, KimsaDatePicker, KimsaCombobox, KimsaUploadFile, KimsaTextField, KimsaTagsPicker, BaseDialog, DialogActions },
    props: {
        currentSelection: {
            type: [Object, String],
            default: null,
        },
        coachId: {
            type: String,
            default: null
        },
        routineNumber: {
            type: Number,
            default: 1,
        },
        editMode: {
            type: Boolean,
            default: false
        },
        fromScratch: {
            type: Boolean,
            default: false
        }
    },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            name: '',
            category: null,
            endDate: null,
            startDate: null,
            description: '',
            file: null,
            tags: [],
            daysPerWeekOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            daysPerWeek: 1,
            programId: null,
            currentImg: null,
            currentImgSavePath: null,
            deletedImage: false,
        }
    },
    computed: {
        ...mapState(['categoriesOptions', "loadingCategories"]),
        disabled(vm = this) {
            if (vm.editMode || vm.fromScratch) return !vm.name || !vm.category
            return !vm.name || !vm.category || !vm.startDate || !vm.endDate;
        },
        previewImg(vm = this) {
            if (vm.file?.img) return vm.file?.img
            if (vm.deletedImage) return null
            return vm.currentImg
        },
        collection(vm = this) {
            return vm.currentSelection?.objectID ? 'assignedRoutines' : 'groupAssignedRoutines'
        },
        categories(vm = this) {
            return [...vm.categoriesOptions, { id: 1, key: 'mix', name: 'Mix', value: 'Mix', label: vm.$t('categories.mix') }]
        }
    },
    async mounted() {
        const vm = this
        if (!vm.categoriesOptions || vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        show(date) {
            const vm = this
            if (vm.editMode) vm.setDefaults(date)
            else if (!vm.fromScratch) vm.startDate = date
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.programId = null
            vm.name = ''
            if (vm.$refs.categories) vm.$refs.categories.model = null
            if (vm.$refs.img) vm.$refs.img.reset()
            vm.currentImg = null
            vm.currentImgSavePath = null
            vm.deletedImage = false
            vm.category = null
            vm.daysPerWeek = 1
            vm.startDate = null
            vm.endDate = null
            vm.description = ''
            vm.file = null
            vm.tags = []
        },
        setDefaults(program) {
            const vm = this
            vm.programId = program.objectID
            vm.name = program.name
            vm.category = vm.categories.find(category => category.key === vm.categoryKey(program.category)) || null
            vm.description = program.description
            vm.tags = program.tags || []
            vm.daysPerWeek = program.daysPerWeek || 1
            vm.currentImg = program.img || null
            vm.currentImgSavePath = program.imgSavePath || null
        },
        async loadRoutines() {
            const vm = this

            if (vm.collection === 'assignedRoutines') {
                return await db.collection(vm.collection)
                    .where('studentId', '==', vm.currentSelection.objectID)
                    .where('rawDate.full', ">=", vm.startDate)
                    .where('rawDate.full', "<=", vm.endDate)
                    .where('routineNumber', '==', vm.routineNumber)
                    .orderBy('rawDate.full', 'asc').get()
            } else {
                return await db.collection(vm.collection)
                    .where('groupId', '==', vm.currentSelection.id)
                    .where('teamId', '==', vm.currentSelection?.teamId || null)
                    .where('rawDate.full', ">=", vm.startDate)
                    .where('rawDate.full', "<=", vm.endDate)
                    .where('routineNumber', '==', vm.routineNumber)
                    .orderBy('rawDate.full', 'asc').get()
            }
        },
        async save() {
            const vm = this
            const { programId, name, category, description, tags, daysPerWeek, file, currentImg, currentImgSavePath, deletedImage } = vm
            await vm.withLoading(async () => {
                if (vm.editMode) {
                    let img = deletedImage ? null : { tempUrl: currentImg, savePath: currentImgSavePath }
                    if (file) img = await vm.$refs.img.upload()
                    let payload = {
                        name: name,
                        category: category?.name || '',
                        categoryKey: vm.categoryKey(category?.name) || '',
                        description: description,
                        tags: tags,
                        daysPerWeek: daysPerWeek,
                        img: img?.tempUrl || '',
                        imgSavePath: img?.savePath || '',
                    }
                    await db.collection('programs').doc(programId).update(payload)
                    vm.hide()
                    vm.$emit('success', { objectID: programId, ...payload })
                    vm.toast(vm.$t('programs.saved'))
                    return
                } else if (vm.fromScratch) {
                    let img = null
                    if (file) img = await vm.$refs.img.upload()

                    let payloadProgram = getPayloadBaseProgram(name, vm.coachId, [], category?.name || '', description, img, tags, daysPerWeek)

                    let ref = await db.collection('programs').add({ ...payloadProgram, used: 0 })

                    vm.hide()
                    vm.$emit('success', ref.id)
                    vm.toast(vm.$t('programs.saved'))
                    return
                } else {
                    let routines = await vm.loadRoutines()
                    routines = routines.docs.map(routine => ({ ...routine.data(), id: routine.id }))

                    let assignedRoutines = [];

                    const uniqueRoutines = uniqueDayRoutine(routines);

                    for (const routine of uniqueRoutines) {
                        assignedRoutines.push({ blocks: clearBlocksAssignedRoutine(routine.blocks) });
                    }

                    let img = null
                    if (file) img = await vm.$refs.img.upload()

                    let payloadProgram = getPayloadBaseProgram(name, vm.coachId, assignedRoutines, category?.name || '', description, img, tags, daysPerWeek)

                    let ref = await db.collection('programs').add(payloadProgram)

                    vm.hide()
                    vm.$emit('success', ref.id)
                    vm.toast(vm.$t('programs.saved'))
                }
            })
        },
        onDeleteImg() {
            const vm = this
            vm.file = null
            vm.deletedImage = true
            vm.$refs.img.reset()
        },
    },

}
</script>

<style scoped>
.dlg-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.prg-grid2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: start;
}

@media (max-width: 480px) {
    .prg-grid2 {
        grid-template-columns: 1fr;
    }
}
</style>